import { createFileRoute } from "@tanstack/react-router"
import { useApiQuery } from "@/api/ApiQueryServiceProvider"
import { KpiGroup } from "@/components/dashboard"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import {
  Network, Settings, Sprout, Sparkles,
  MapPin, Building2, Zap, Bot, Users, Leaf, Home, Handshake, Package,
  LayoutDashboard,
} from "lucide-react"

// ============================================================================
// Route Definition
// ============================================================================

// @ts-ignore - Route type generation issue in Jest
export const Route = createFileRoute("/home")({
  staticData: {
    title: 'Dashboard',
    isSecured: true,
  },
  component: DashboardPage,
})

// ============================================================================
// Dashboard Page Component
// ============================================================================

/**
 * Dashboard Page Component
 *
 * Displays key performance indicators (KPIs) organized into 4 groups:
 * - Network Deployment: Corridors and Sites
 * - Device Integration: Sensors and Robots
 * - Farmgrid: Users, Acres, and Farms
 * - Ecosystem: Solution Partners and Marketplace Solutions
 *
 * Features:
 * - Real-time data fetching from API
 * - Loading state with skeleton placeholders
 * - Error state with retry functionality
 * - Formatted numbers with comma separators
 */
function DashboardPage() {
  // Fetch KPI data from API
  // In development, mocks are used transparently when configured in apiRoutes.yml
  const { data: dashboardData, isLoading, isError, refetch, dataUpdatedAt } = useApiQuery('getDashboardKpis', {})

  // KPI Goals by Year (Y1, Y2, Y3)
  const kpiGoals = {
    networkDeployment: {
      corridors: { y1: 10, y2: 20, y3: 45 },
      sites: { y1: 6, y2: 25, y3: 312 },
    },
    deviceIntegration: {
      sensors: { y1: 12500, y2: 31250, y3: 56250 },
      robots: { y1: 500, y2: 1250, y3: 2250 },
    },
    farmgrid: {
      users: { y1: 750, y2: 1875, y3: 3375 },
      acres: { y1: 17500, y2: 236250, y3: 945000 },
      farms: { y1: 250, y2: 625, y3: 1125 },
    },
    ecosystem: {
      partners: { y1: 10, y2: 20, y3: 30 },
      solutions: { y1: 20, y2: 80, y3: 180 },
    },
  }

  // Current year for goal display (can be made dynamic)
  const currentYear = 'y1' as const

  // Transform API response into KPI groups structure with goals
  const networkDeploymentKpis = [
    {
      key: 'corridors',
      label: 'Number of Corridors',
      actual: dashboardData?.networkDeployment?.numberOfCorridors,
      goal: kpiGoals.networkDeployment.corridors[currentYear],
      icon: MapPin,
    },
    {
      key: 'sites',
      label: 'Number of Sites',
      actual: dashboardData?.networkDeployment?.numberOfSites,
      goal: kpiGoals.networkDeployment.sites[currentYear],
      icon: Building2,
    },
  ]

  const deviceIntegrationKpis = [
    {
      key: 'sensors',
      label: 'Number of Sensors',
      actual: dashboardData?.deviceIntegration?.numberOfSensors,
      goal: kpiGoals.deviceIntegration.sensors[currentYear],
      icon: Zap,
    },
    {
      key: 'robots',
      label: 'Number of Robots',
      actual: dashboardData?.deviceIntegration?.numberOfRobots,
      goal: kpiGoals.deviceIntegration.robots[currentYear],
      icon: Bot,
    },
  ]

  const farmgridKpis = [
    {
      key: 'users',
      label: 'Number of Users',
      actual: dashboardData?.farmgrid?.numberOfUsers,
      goal: kpiGoals.farmgrid.users[currentYear],
      icon: Users,
    },
    {
      key: 'acres',
      label: 'Acres Under Management',
      actual: dashboardData?.farmgrid?.acresUnderManagement,
      goal: kpiGoals.farmgrid.acres[currentYear],
      icon: Leaf,
    },
    {
      key: 'farms',
      label: 'Farms Under Contract',
      actual: dashboardData?.farmgrid?.farmsUnderContract,
      goal: kpiGoals.farmgrid.farms[currentYear],
      icon: Home,
    },
  ]

  const ecosystemKpis = [
    {
      key: 'partners',
      label: 'Solution Partners Integrated',
      actual: dashboardData?.ecosystem?.solutionPartnersIntegrated,
      goal: kpiGoals.ecosystem.partners[currentYear],
      icon: Handshake,
    },
    {
      key: 'solutions',
      label: 'Solutions in Marketplace',
      actual: dashboardData?.ecosystem?.solutionsInMarketplace,
      goal: kpiGoals.ecosystem.solutions[currentYear],
      icon: Package,
    },
  ]

  const updatedAt = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleDateString('en-US', {
        year: 'numeric', month: 'long', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    : null

  return (
    <div className="p-6 bg-slate-50 min-h-screen">
      {/* Page Header */}
      <Card className="shadow-sm pb-0 overflow-hidden mb-8">
        <CardHeader className="bg-slate-700 text-white [&_p]:text-slate-300 pb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <LayoutDashboard className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-xl">Key Performance Indicators</CardTitle>
                <CardDescription className="text-base">Real-time metrics and performance overview</CardDescription>
              </div>
            </div>
            <div className="flex flex-col items-start gap-1 sm:items-end shrink-0">
              {updatedAt && <p className="text-sm text-slate-300">Updated: {updatedAt}</p>}
              <p className="text-xs text-slate-400">Version {__APP_VERSION__}</p>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Error State */}
      {isError && (
        <Card className="shadow-sm">
          <CardContent className="flex flex-col items-center justify-center py-12 gap-4">
            <p className="text-destructive">Failed to load dashboard data</p>
            <Button variant="outline" onClick={() => refetch()}>
              Try Again
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Loading State or KPI Groups */}
      {!isError && (
        <div className="space-y-0">
          {/* Group 1: Network Deployment */}
          <KpiGroup
            title="Network Deployment"
            icon={Network}
            kpis={networkDeploymentKpis}
            isLoading={isLoading}
          />

          {/* Group 2: Device Integration */}
          <KpiGroup
            title="Device Integration"
            icon={Settings}
            kpis={deviceIntegrationKpis}
            isLoading={isLoading}
          />

          {/* Group 3: Farmgrid */}
          <KpiGroup
            title="Farmgrid"
            icon={Sprout}
            kpis={farmgridKpis}
            isLoading={isLoading}
          />

          {/* Group 4: Ecosystem */}
          <KpiGroup
            title="Ecosystem"
            icon={Sparkles}
            kpis={ecosystemKpis}
            isLoading={isLoading}
          />
        </div>
      )}
    </div>
  )
}
